import { ReactElement, ReactNode } from 'react';
import { XM_FONT_SIZE, XM_FONT_STACK } from './canvasUtils';
import { renderToMeasureHTML } from './renderStatic';

const CELL_PADDING_PX = 6; // mirrors the Table `cellPaddingInlineSM` token (ThemeProvider)
export const cellPaddingPx = () => CELL_PADDING_PX * 2;

const CELL_BORDER_PX = 1;
export const cellBorderPx = () => CELL_BORDER_PX;

export interface MeasureCellItem {
  key: string;
  content: ReactNode;
  /** Pre-rendered HTML to use as innerHTML, skipping ReactDOMServer at measure time. */
  innerHTML?: string;
  styleClass?: string;
  newLines?: boolean;
  /**
   * Font weight to measure with. Defaults to 400 (body cell). A header renders at 600 —
   * `--xm-font-weight-medium`, applied in _table.less, NOT an antd default — and a bold
   * value cell (styleClass "bold") at 700. Measuring at the weight the cell actually
   * renders keeps the width from clipping.
   */
  fontWeight?: number;
}

/**
 * Measure the rendered width each column needs to fit its content without
 * clipping. Builds a single hidden `<table class="ant-table">` row off-screen,
 * lets the browser do real DOM layout, and reads `getBoundingClientRect().width`
 * per cell.
 */
export function measureColumnWidths(items: MeasureCellItem[], fontScale = 1): Record<string, number> {
  if (!items.length) return {};

  const fontSize = XM_FONT_SIZE * fontScale;
  // Only the inline half has to match a real cell — the block value never affects width, so
  // it stays nominal instead of mirroring cellPaddingBlockSM (which our CSS overrides per
  // DOM variant anyway).
  const padding = `4px ${cellPaddingPx() / 2}px`;

  // Measure with the canonical cell font, NOT the resolved font of a reference
  // cell. Reading from `document.querySelector('td.ant-table-cell')` made the
  // result depend on the DOM state at measure time: on a fresh refresh no cell
  // exists yet (it fell back to document.body's font), while navigating in found
  // a real cell — two different fonts, so the same table measured to different
  // column widths depending on how you arrived. XM_FONT_STACK is the antd default
  // stack (the theme sets no custom fontFamily), verified against a live cell's
  // computed font; being per-OS, each platform picks the same native font the cell
  // renders with (incl. -apple-system on macOS). The canvas trim-check builds its
  // font the same way, so DOM and canvas measurements agree, deterministically.
  // Size and weight both vary per call: fontScale for a table rendering below the
  // 12px base (GraphTable's 0.9em), weight 400 body / 600 header / 700 bold cell.

  const container = document.createElement('div');
  container.style.cssText = 'position:absolute;visibility:hidden;left:-9999px;top:-9999px;pointer-events:none';

  const table = document.createElement('table');
  table.className = 'ant-table';
  table.style.cssText = 'table-layout:auto;border-collapse:separate;border-spacing:0';

  const tbody = document.createElement('tbody');
  tbody.className = 'ant-table-tbody';

  const tr = document.createElement('tr');
  tr.className = 'ant-table-row';

  const tds: { key: string; td: HTMLElement; }[] = [];
  for (const item of items) {
    const td = document.createElement('td');
    td.className = ['ant-table-cell', item.styleClass].filter(Boolean).join(' ');

    const whiteSpace = item.newLines ? 'pre-line' : 'nowrap';
    const font = `${item.fontWeight ?? 400} ${fontSize}px ${XM_FONT_STACK}`;
    td.style.cssText = `font:${font};padding:${padding};white-space:${whiteSpace};box-sizing:border-box`
      + `;border-right:${CELL_BORDER_PX}px solid transparent`;

    const content = item.content;
    if (item.innerHTML !== undefined) {
      td.innerHTML = item.innerHTML;
    } else if (content == null) {
      // leave td empty
    } else if (typeof content === 'string' || typeof content === 'number') {
      td.textContent = String(content);
    } else {
      try {
        td.innerHTML = renderToMeasureHTML(content as ReactElement);
      } catch {
        td.textContent = String(content);
      }
    }

    tr.appendChild(td);
    tds.push({ key: item.key, td });
  }

  tbody.appendChild(tr);
  table.appendChild(tbody);
  container.appendChild(table);
  document.body.appendChild(container);

  const widths: Record<string, number> = {};
  for (const { key, td } of tds) {
    widths[key] = Math.ceil(td.getBoundingClientRect().width);
  }

  document.body.removeChild(container);
  return widths;
}
